import { assertNever } from '../../exhaustive';
import { CHANNEL_KIND } from '../catalog/channel';
import type { ChannelRef } from '../catalog/channel';
import { CONTROL_KIND } from '../catalog/control';
import { DIN_VALUE, RELAY_VALUE, firmwareChannel } from '../catalog/dinValue';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import { RCU_GROUP, moduleIdOfRail } from '../catalog/protocol';
import type { RcuConfig, WiredLoad } from '../model';
import { dinKey, relayKey } from './live';
import type { LiveState } from './live';

/**
 * What the device last said about one load.
 *
 * `at` is when that reading arrived. A guest action taken after it has not been
 * answered yet, so the control keeps showing what the guest asked for instead of
 * flicking back to a value read before they touched it.
 */
export interface LoadObservation {
  readonly power: boolean;
  /** Level for a dimmer or fan; null for a control that has no level. */
  readonly level: number | null;
  readonly at: string;
}

/** The value the device last reported for a load's channel, or null if none. */
const channelValue = (
  channel: ChannelRef,
  config: RcuConfig,
  live: LiveState,
): { readonly value: number; readonly at: string } | null => {
  switch (channel.kind) {
    case CHANNEL_KIND.RCU: {
      const key = relayKey(RCU_GROUP.DEFAULT, channel.channel);
      const value = live.relays[key];
      const at = live.relayAt[key];
      return value === undefined || at === undefined ? null : { value, at };
    }

    case CHANNEL_KIND.DIN: {
      const rail = config.rails.find((entry) => entry.id === channel.railId);
      if (rail === undefined) return null;

      /**
       * The configuration counts channels from one, the firmware from zero, and
       * a reply carries the firmware's own numbering.
       */
      const key = dinKey(moduleIdOfRail(rail.type), rail.address, firmwareChannel(channel.channel));
      const value = live.din[key];
      const at = live.dinAt[key];
      return value === undefined || at === undefined ? null : { value, at };
    }

    default:
      return assertNever(channel);
  }
};

/** The state a guest left a control in, and when they left it. */
export interface GuestIntent {
  readonly power: boolean;
  readonly level: number;
  /** Epoch milliseconds of the guest's last touch; 0 when they never touched it. */
  readonly at: number;
}

/**
 * What a control should draw.
 *
 * The device wins as soon as it has said anything about this channel after the
 * guest's last touch — that is the whole point of the link. Until then the
 * guest's own intent stands, so a tap is not undone by a reading that arrived
 * before it, and a channel the device never reports keeps working as it did.
 *
 * The comparison is arrival times, because the protocol carries no sequence
 * numbers and no sampling time. One case stays ambiguous: a re-delivered message
 * holding a pre-tap value (QoS 1 permits duplicates, and a snapshot answer can
 * cross a tap on the wire) arrives after the tap and is believed until the next
 * report for that channel. Nothing short of a per-channel sequence from the
 * firmware could settle it.
 */
export const resolveLoadState = (
  observation: LoadObservation | null,
  intent: GuestIntent,
): { readonly power: boolean; readonly level: number } => {
  if (observation === null || Date.parse(observation.at) <= intent.at) {
    return { power: intent.power, level: intent.level };
  }
  return { power: observation.power, level: observation.level ?? intent.level };
};

/**
 * Reads a load's state out of what the link has heard. Null means the device has
 * never reported this channel, which is not the same as off: a control with no
 * observation keeps the state the guest left it in and claims nothing.
 *
 * Motors report no position — a curtain module is told a direction, and the
 * reference does not say what its own 0/1/2 replies mean — so they never produce
 * an observation.
 */
export const observeLoad = (
  load: WiredLoad,
  config: RcuConfig,
  live: LiveState,
): LoadObservation | null => {
  const reading = channelValue(load.channel, config, live);
  if (reading === null) return null;

  const { control } = LOAD_TYPE_SPECS[load.type];
  switch (control) {
    case CONTROL_KIND.TOGGLE:
      return { power: reading.value !== RELAY_VALUE.OFF, level: null, at: reading.at };

    case CONTROL_KIND.LEVEL: {
      /** Past 100 the value is one of the direction codes, not a level. */
      if (reading.value > DIN_VALUE.MAX) return null;
      return {
        power: reading.value > DIN_VALUE.MIN,
        level: reading.value,
        at: reading.at,
      };
    }

    case CONTROL_KIND.POSITION:
      return null;

    default:
      return assertNever(control);
  }
};
